import React from 'react';
import PropTypes from 'prop-types';

import {Box, Paper, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

import formatters from '../shared/formatters';

import {FLAG_ACCENT_COLOR, FLAG_ROW_BACKGROUND, getUnpricedHours, summaryGridColumns} from './clientMonthlyShared';

// Compact, comparison-free stat cards. Values come straight from the backend `totals`,
// which cover every client in the snapshot rather than only the paginated rows.
const buildSummaryCards = (totals) => {
  const unpricedHours = getUnpricedHours(totals);
  const missingRateCount = formatters.isMissingValue(totals?.clientsMissingRate) ? 0 : totals.clientsMissingRate;

  return [
    {
      key: 'activeClients',
      labelExpression: 'metrics.ClientMetrics.ClientMonthly.Summary.ActiveClients',
      value: formatters.formatCount(totals?.activeClientCount),
    },
    {
      key: 'clientsWithHours',
      labelExpression: 'metrics.ClientMetrics.ClientMonthly.Summary.ClientsWithHours',
      value: formatters.formatCount(totals?.clientsWithScheduledHours),
    },
    {
      key: 'scheduledHours',
      labelExpression: 'metrics.ClientMetrics.ClientMonthly.Summary.ScheduledHours',
      value: formatters.formatHours(totals?.scheduledHours),
    },
    {
      key: 'weightedHours',
      labelExpression: 'metrics.ClientMetrics.ClientMonthly.Summary.WeightedHours',
      value: formatters.formatHours(totals?.weightedHours),
    },
    {
      key: 'estimatedRevenue',
      labelExpression: 'metrics.ClientMetrics.ClientMonthly.Summary.EstimatedRevenue',
      value: formatters.formatCurrency(totals?.estimatedRevenue),
    },
    {
      key: 'missingRates',
      labelExpression: 'metrics.ClientMetrics.ClientMonthly.Summary.MissingRates',
      value: formatters.formatCount(totals?.clientsMissingRate),
      caption: unpricedHours > 0 ?
        translate('metrics.ClientMetrics.ClientMonthly.Summary.UnpricedHoursCaption', {hours: formatters.formatHours(unpricedHours)}) :
        translate('metrics.ClientMetrics.ClientMonthly.Summary.NoUnpricedHours'),
      warn: missingRateCount > 0 || unpricedHours > 0,
    },
  ];
};

const ClientMonthlySummaryCards = (props) => {
  const {totals} = props;

  const cards = React.useMemo(() => buildSummaryCards(totals), [totals]);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: summaryGridColumns,
        gap: 1.75,
      }}
    >
      {
        cards.map((card) => (
          <Paper
            key={card.key}
            sx={{
              p: 2,
              borderRadius: '8px',
              border: card.warn ? '1px solid #F0D6B0' : '1px solid #E5E5E5',
              backgroundColor: card.warn ? FLAG_ROW_BACKGROUND : '#FFFFFF',
              boxShadow: 'none',
            }}
          >
            <Typography
              sx={{
                fontSize: '10px',
                fontWeight: 600,
                lineHeight: '13px',
                letterSpacing: '0.04em',
                color: card.warn ? FLAG_ACCENT_COLOR : '#A8A49E',
                mb: 1,
              }}
            >
              <Translate expression={card.labelExpression} />
            </Typography>
            <Typography sx={{fontSize: '20px', fontWeight: 600, lineHeight: '26px', color: '#1A1916'}}>
              {card.value}
            </Typography>
            {
              card.caption &&
              <Typography sx={{fontSize: '10px', lineHeight: '13px', color: '#A8A49E', mt: 0.5}}>
                {card.caption}
              </Typography>
            }
          </Paper>
        ))
      }
    </Box>
  );
};

ClientMonthlySummaryCards.propTypes = {
  totals: PropTypes.object,
};

export default React.memo(ClientMonthlySummaryCards);
